import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, ChevronDown, ChevronUp } from './icons';
import { CATALOGUE_ENABLED } from '../config/features';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const footerSections = [
    {
      key: 'company',
      title: 'Company',
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'Our Team', href: '/about#team' },
        { name: 'Careers', href: '/careers' },
        { name: 'Contact', href: '/contact' },
      ]
    },
    {
      key: 'products',
      title: 'Products',
      links: [
        { name: 'Clinical Materials', href: '/catalogue?category=clinical-materials' },
        { name: 'Machines & Equipment', href: '/catalogue?category=machines-equipment' },
        { name: 'Laboratory Materials', href: '/catalogue?category=laboratory-materials' },
        { name: 'New Arrivals', href: '/catalogue?sort=newest' },
      ]
    },
    {
      key: 'support',
      title: 'Support',
      links: [
        { name: 'Help Center', href: '/help' },
        { name: 'Technical Support', href: '/support' },
        { name: 'Warranty', href: '/warranty' },
        { name: 'Returns', href: '/returns' },
      ]
    },
    {
      key: 'legal',
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', href: '/privacy' },
        { name: 'Terms of Service', href: '/terms' },
        { name: 'Shipping Policy', href: '/shipping' },
        { name: 'Refund Policy', href: '/refund' },
      ]
    }
  ];

  // Hide the Products column while the Catalogue is in development.
  const visibleSections = footerSections.filter(s => CATALOGUE_ENABLED || s.key !== 'products');

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: 'https://facebook.com/dentopharma' },
    { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/dentopharma/' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/dentopharma' },
  ];

  return (
    <footer className="bg-[#0D56A6] text-white">
      {/* Main Footer Content */}
      <div className="container-custom py-6 sm:py-12">
        {/* Contact Info - Always Visible */}
        <div className="mb-5 sm:mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-4 text-sm">
            <div className="flex items-center space-x-3 text-white group">
              <div className="w-8 h-8 bg-[#8EC641]/10 rounded-full flex items-center justify-center group-hover:bg-[#8EC641]/20 transition-colors duration-300">
                <MapPin size={16} className="text-[#8EC641]" />
              </div>
              <span className="text-sm">India Street, Dar es Salaam, Tanzania</span>
            </div>
            <div className="flex items-center space-x-3 text-white group">
              <div className="w-8 h-8 bg-[#8EC641]/10 rounded-full flex items-center justify-center group-hover:bg-[#8EC641]/20 transition-colors duration-300">
                <Phone size={16} className="text-[#8EC641]" />
              </div>
              <span className="text-sm">+255 754 690 738</span>
            </div>
            <div className="flex items-center space-x-3 text-white group">
              <div className="w-8 h-8 bg-[#8EC641]/10 rounded-full flex items-center justify-center group-hover:bg-[#8EC641]/20 transition-colors duration-300">
                <Mail size={16} className="text-[#8EC641]" />
              </div>
              <span className="text-sm">info@dentopharma.co.tz</span>
            </div>
          </div>
        </div>

        {/* Collapsible Links - Mobile Only */}
        <div className="sm:hidden">
          {visibleSections.map((section) => {
            const isExpanded = expandedSections.includes(section.key);
            return (
              <div key={section.key} className="border-b border-white/10">
                <button
                  onClick={() => toggleSection(section.key)}
                  className="w-full flex items-center justify-between py-3 text-left"
                >
                  <span className="font-semibold text-white">{section.title}</span>
                  {isExpanded ? (
                    <ChevronUp size={20} className="text-white/60" />
                  ) : (
                    <ChevronDown size={20} className="text-white/60" />
                  )}
                </button>
                {isExpanded && (
                  <div className="pb-4">
                    <ul className="space-y-2">
                      {section.links.map((link) => (
                        <li key={link.name}>
                          <Link
                            to={link.href}
                            className="block py-1 text-white/80 hover:text-[#8EC641] transition-colors duration-200"
                          >
                            {link.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop Links - Hidden on Mobile */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {visibleSections.map((section) => (
            <div key={section.key}>
              <h3 className="text-lg font-semibold mb-4 text-white">{section.title}</h3>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-white/80 hover:text-[#8EC641] transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter Signup - Compact on Mobile */}
        <div className="mt-5 pt-5 sm:mt-8 sm:pt-6 border-t border-white/20">
          <div className="max-w-md">
            <h3 className="text-lg font-semibold mb-3 text-white">Stay Updated</h3>
            <p className="text-white/80 mb-4 text-sm sm:text-base">
              Subscribe to our newsletter for the latest products and industry updates.
            </p>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/60 focus:ring-2 focus:ring-[#8EC641] focus:border-transparent text-sm"
              />
              <button className="bg-[#8EC641] hover:bg-[#7DB535] text-white font-semibold px-6 py-2 rounded-2xl transition-colors duration-200 text-sm sm:text-base">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer - Compact */}
      <div className="border-t border-white/20">
        <div className="container-custom py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
            {/* Copyright */}
            <div className="text-white/70 text-xs sm:text-sm text-center sm:text-left">
              © {currentYear} Dentopharma Supplies Limited. All rights reserved.
            </div>

            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center text-[#0D56A6] hover:scale-110 transition-all duration-200 shadow-sm"
                  title={social.name}
                >
                  <social.icon size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
